/**
 * Permisos REALES del usuario, tal como los entrega el backend en `GET /me/access` (Fase 5).
 *
 * Antes, AuthProvider.toMyAuthz inventaba los permisos con solo tres casos (institucional / auditor / "todos los
 * demas = colaborador"), asi que un Revisor RAT o un usuario de Consulta se trataban como Colaboradores. Ahora el
 * backend dice, para cada permiso, DONDE aplica: 'ALL' (global) o la lista exacta de unidades. Este modulo:
 *   - expone consultas con alcance (`canIn`, `hasAnywhere`) para las pantallas que conocen la unidad, y
 *   - deriva el `MyAuthz` de siempre (vocabulario viejo de @rat/shared) para que las paginas que solo preguntan
 *     "puede hacer X en alguna parte" sigan funcionando sin cambios.
 *
 * Es solo capa 1 (UX): el backend revalida en cada request.
 */
import type { MyAuthz, Permission } from '@rat/shared';

export type RatRole = 'admin_rat' | 'revisor_rat' | 'jefe_unidad' | 'colaborador' | 'consulta' | 'auditor';
export type AccessScope = 'ALL' | string[];

export interface RoleAssignmentDto {
  id: string;
  role: RatRole;
  scopeType: 'global' | 'unit_type' | 'unit';
  scopeId: string | null;
  includeDescendants: boolean;
}

/** Forma de `GET /me/access`: asignaciones de rol y, por permiso, 'ALL' o las unidades donde aplica. */
export interface AccessResponse {
  assignments: RoleAssignmentDto[];
  permissions: Record<string, AccessScope>;
}

export const EMPTY_ACCESS: AccessResponse = { assignments: [], permissions: {} };

/** ¿Tiene el permiso en ALGUNA parte? Falla cerrada: un alcance vacio no cuenta. */
export function hasAnywhere(access: AccessResponse | null | undefined, perm: string): boolean {
  const s = access?.permissions[perm];
  return s === 'ALL' || (Array.isArray(s) && s.length > 0);
}

/** ¿Tiene el permiso EN esa unidad? Es la pregunta correcta para cualquier control ligado a una unidad. */
export function canIn(access: AccessResponse | null | undefined, perm: string, unitId: string | null | undefined): boolean {
  if (!unitId) return false;
  const s = access?.permissions[perm];
  return s === 'ALL' || (Array.isArray(s) && s.includes(unitId));
}

const isAll = (access: AccessResponse, perm: string): boolean => access.permissions[perm] === 'ALL';

/**
 * Vocabulario viejo (@rat/shared `Permission`) derivado de los permisos nuevos. Regla: "en alguna parte" para los
 * permisos de accion; 'ALL' distingue lo global (`*.all`) de lo acotado a unidades (`*.own_unit`).
 */
export function deriveLegacyPermissions(access: AccessResponse): Permission[] {
  const out = new Set<Permission>();
  const any = (p: string) => hasAnywhere(access, p);

  if (isAll(access, 'activity.read')) out.add('activity.read.all');
  else if (any('activity.read')) out.add('activity.read.own_unit');

  if (any('activity.create')) out.add('activity.create');
  if (isAll(access, 'activity.update')) {
    out.add('activity.update.all');
    out.add('activity.update.own_unit');
  } else if (any('activity.update')) out.add('activity.update.own_unit');
  if (any('activity.submit')) out.add('activity.submit');
  if (any('activity.review')) out.add('activity.review');
  if (any('activity.close')) out.add('activity.close');
  if (any('activity.reopen')) out.add('activity.reopen');

  // 'institutional.view' gatea Catalogos/Usuarios y el tablero del administrador: es del Administrador RAT.
  if (any('dashboard.admin') || any('user.manage')) out.add('institutional.view');
  // El auditor (y solo el) lee todo sin administrar: 'unit.read.all' le da el tablero institucional y los reportes.
  if (any('dashboard.auditor') && !any('dashboard.admin')) out.add('unit.read.all');
  if (any('audit.read')) out.add('audit.read');
  if (any('user.manage')) out.add('user.manage');
  if (any('tracking.manage')) out.add('tracking.manage');
  return [...out];
}

/** Lo que /me devuelve de la persona y sus unidades asignadas (con su unit_role, que el backend deriva de los roles). */
export interface MeLike {
  userId: string;
  units: Array<{ unit_id: string; name: string; unit_role: 'jefe' | 'colaborador' | 'consulta' }>;
}

export function deriveMyAuthz(me: MeLike, access: AccessResponse): MyAuthz {
  return {
    user_id: me.userId,
    // Siempre true: a este punto hay una sesion REAL (el segundo factor, si aplicaba, ya se completo).
    mfa: true,
    // "Administrador": el unico rol con user.manage. No es un bypass: las decisiones por unidad usan `canIn`.
    institutional: hasAnywhere(access, 'user.manage'),
    permissions: deriveLegacyPermissions(access),
    units: me.units.map((u) => ({
      unit_id: u.unit_id,
      code: u.unit_id,
      name: u.name,
      unit_role: u.unit_role,
      includes_descendants: false,
    })),
  };
}

const ROLE_LABEL: Record<RatRole, string> = {
  admin_rat: 'Administrador RAT / DPD',
  revisor_rat: 'Revisor RAT',
  auditor: 'Auditor / Consulta',
  jefe_unidad: 'Responsable de Unidad',
  colaborador: 'Colaborador',
  consulta: 'Consulta',
};

/** De mayor a menor: la etiqueta de quien tiene varios roles es la del de mayor responsabilidad. */
const ROLE_PRIORITY: readonly RatRole[] = ['admin_rat', 'revisor_rat', 'auditor', 'jefe_unidad', 'colaborador', 'consulta'];

/** Etiqueta de un rol por su codigo (para el hilo de observaciones, donde cada entrada guarda el rol con que actuo). */
export function ratRoleLabel(role: string): string {
  return ROLE_LABEL[role as RatRole] ?? role;
}

export function roleLabel(access: AccessResponse | null | undefined): string {
  const roles = new Set((access?.assignments ?? []).map((a) => a.role));
  const top = ROLE_PRIORITY.find((r) => roles.has(r));
  return top ? ROLE_LABEL[top] : 'Usuario';
}

/** ¿Es (solo) revisor? Sirve para el aterrizaje: no tiene unidad propia pero SI trabajo asignado. */
export function isReviewer(access: AccessResponse | null | undefined): boolean {
  return hasAnywhere(access, 'activity.review');
}
